/**
 * New projects from a template or as a remix of another project (vfs-service `/api/v1/templates`,
 * `/api/v1/projects/:id/template`, `/api/v1/projects/:id/remix`), with the person's authorized fetch as the Data tab's
 * calls (DataService). The project itself is created first as any new project (utils/quickProjectCreation.js); these
 * calls then fill it.
 *
 * Template: `{ id, name, description, category: 'app' | 'starter', includes: [text], stacks, stores, services, preview
 * (a vfs-relative URL of an SVG), tags }`.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { DataError, fail } from './DataService';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const seg = (value) => encodeURIComponent(String(value));

async function call(path, { method = 'GET', body, accept = 'application/json' } = {}) {
  if (!base()) { throw new DataError('vfs-service is not configured', 0, null); }
  const response = await getAuthorizedFetch()(`${base()}${path}`, {
    method,
    headers: { Accept: accept, ...buildAclContextHeaders(), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) { throw await fail(response); }
  return response;
}

const json = async (response) => (await response.json().catch(() => null)) || {};

/** `{ templates: [Template] }`. */
export async function listTemplates() {
  return json(await call('/api/v1/templates'));
}

/** The full URL of a template's preview (its `preview` is relative to vfs-service). */
export function previewUrl(preview) {
  if (!preview) { return null; }
  if (/^https?:\/\//i.test(preview)) { return preview; }
  return `${base()}${preview.startsWith('/') ? '' : '/'}${preview}`;
}

/** The preview image as a Blob, fetched with the person's token (vfs-service may need it). */
export async function getPreviewBlob(preview) {
  const url = previewUrl(preview);
  if (!url) { throw new DataError('No preview', 0, null); }
  const response = await getAuthorizedFetch()(url, { method: 'GET', headers: { Accept: 'image/svg+xml,image/*', ...buildAclContextHeaders() } });
  if (!response.ok) { throw await fail(response); }
  return response.blob();
}

/** Fills a fresh project from a template: `{ template: { id, name }, files, services: ['services/api', ...] }`. */
export async function applyTemplate(projectId, template) {
  return json(await call(`/api/v1/projects/${seg(projectId)}/template`, { method: 'POST', body: { template } }));
}

/** What a remix of `sourceId` would copy: `{ projectId, name, description, owner: { name, email }, services, files, seedFiles }`. */
export async function getRemixInfo(sourceId) {
  return json(await call(`/api/v1/projects/${seg(sourceId)}/remix/info`));
}

/**
 * Copies the source project's files into the fresh project `targetId` (never its data): `{ remixedFrom, files, services,
 * seedFiles }`. `includeSeedData`: also the sample rows of its db scripts.
 */
export async function remixInto(targetId, sourceId, { includeSeedData = false } = {}) {
  return json(await call(`/api/v1/projects/${seg(targetId)}/remix`, { method: 'POST', body: { source: sourceId, includeSeedData: Boolean(includeSeedData) } }));
}
